import * as React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  CssBaseline,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Toolbar,
  Typography,
  CircularProgress,
  Alert,
  Link,
  TextField,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
} from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncIcon from "@mui/icons-material/Sync";
import AddIcon from "@mui/icons-material/Add";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import BusinessIcon from "@mui/icons-material/Business";
import Footer from "../components/Footer";
import CreateSquarespaceOrderDialog from "../components/admin/CreateSquarespaceOrderDialog";
import Navigation from "../components/Navigation";
import CancelSubscriptionDialog from "../components/admin/CancelSubscriptionDialog";
import ReactivateSubscriptionDialog from "../components/admin/ReactivateSubscriptionDialog";
import AutoRenewConfirmDialog from "../components/admin/AutoRenewConfirmDialog";
import SyncFromSquarespaceConfirmDialog from "../components/admin/SyncFromSquarespaceConfirmDialog";
import OverrideStatusDialog from "../components/admin/OverrideStatusDialog";
import EditMetaDataDialog from "../components/admin/EditMetaDataDialog";
import JsonDataModal from "../components/admin/JsonDataModal";
import { SUBSCRIPTION_SOURCES } from "../constants/admin";
import { useApi } from "../hooks/useApi";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const toDateInputValue = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toISOString().slice(0, 16);
};

function parseSubscriptionMetaData(raw) {
  if (raw != null && typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return p && typeof p === "object" && !Array.isArray(p) ? p : null;
    } catch {
      return null;
    }
  }
  return null;
}

function metaDataIdString(meta, key) {
  if (!meta) return null;
  const v = meta[key];
  if (v == null) return null;
  const s = String(v).trim();
  return s || null;
}

const SUBSCRIPTION_META_SS_ADMIN_LINKS = [
  {
    key: "squarespace_customer_id",
    label: "View SS Customer",
    to: (id) => `/dashboard/squarespace/customers/${encodeURIComponent(id)}`,
  },
  {
    key: "squarespace_order_id",
    label: "View SS Order",
    to: (id) => `/dashboard/squarespace/orders/${encodeURIComponent(id)}`,
  },
  {
    key: "squarespace_subscription_id",
    label: "View SS Subscription",
    to: (id) => `/dashboard/squarespace/subscriptions/${encodeURIComponent(id)}`,
  },
  {
    key: "squarespace_website_id",
    label: "View SS Website",
    to: (id) => `/dashboard/squarespace/websites/${encodeURIComponent(id)}`,
  },
];

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AdminSubscriptionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const api = useApi();

  const getInitialTab = () => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 1) {
        return tabIndex;
      }
    }
    return 0;
  };
  const [tabValue, setTabValue] = React.useState(getInitialTab);
  const handleTabChange = (e, newValue) => {
    setTabValue(newValue);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", String(newValue));
      return next;
    });
  };
  const [subscription, setSubscription] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [orgContacts, setOrgContacts] = React.useState([]);
  const [loadingContacts, setLoadingContacts] = React.useState(false);
  const [jsonModalOpen, setJsonModalOpen] = React.useState(false);
  const [updating, setUpdating] = React.useState(false);
  const [updateError, setUpdateError] = React.useState(null);
  const [metaDataModalOpen, setMetaDataModalOpen] = React.useState(false);
  const [editMetaData, setEditMetaData] = React.useState("");
  const [updatingMetaData, setUpdatingMetaData] = React.useState(false);
  const [metaDataError, setMetaDataError] = React.useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = React.useState(false);
  const [reactivating, setReactivating] = React.useState(false);
  const [autoRenewConfirmOpen, setAutoRenewConfirmOpen] = React.useState(false);
  const [autoRenewPendingValue, setAutoRenewPendingValue] = React.useState(null);
  const [updatingAutoRenew, setUpdatingAutoRenew] = React.useState(false);
  const [ssStatus, setSsStatus] = React.useState(null);
  const [ssStatusLoading, setSsStatusLoading] = React.useState(false);
  const [syncFromSsLoading, setSyncFromSsLoading] = React.useState(false);
  const [syncFromSsConfirmOpen, setSyncFromSsConfirmOpen] = React.useState(false);
  const [ssStatusError, setSsStatusError] = React.useState(null);
  const [ssJsonModalOpen, setSsJsonModalOpen] = React.useState(false);
  const [createOrderDialogOpen, setCreateOrderDialogOpen] = React.useState(false);
  const [createOrderFormData, setCreateOrderFormData] = React.useState({
    customerId: "",
    pricingId: "",
    title: "",
    domain: "",
  });
  const [creatingOrder, setCreatingOrder] = React.useState(false);
  const [createOrderError, setCreateOrderError] = React.useState(null);
  const [createOrderResponse, setCreateOrderResponse] = React.useState(null);
  const [findSubscriptionLoading, setFindSubscriptionLoading] = React.useState(false);
  const [findSubscriptionError, setFindSubscriptionError] = React.useState(null);
  const [provisioningContactId, setProvisioningContactId] = React.useState("");
  const [provisioningLinkLoading, setProvisioningLinkLoading] = React.useState(false);
  const [provisioningLinkError, setProvisioningLinkError] = React.useState(null);
  const [provisioningLinkErrorDetail, setProvisioningLinkErrorDetail] = React.useState(null);
  const [createContactFormData, setCreateContactFormData] = React.useState({
    first_name: "",
    last_name: "",
    email: "",
  });
  const [creatingContact, setCreatingContact] = React.useState(false);

  // Edit form state
  const [editAmount, setEditAmount] = React.useState("");
  const [editPaymentSystem, setEditPaymentSystem] = React.useState("");
  const [editPaymentMethodId, setEditPaymentMethodId] = React.useState("");
  const [editStartDate, setEditStartDate] = React.useState("");
  const [editEndDate, setEditEndDate] = React.useState("");
  const [editRenewalDate, setEditRenewalDate] = React.useState("");
  const [editNextBillingDate, setEditNextBillingDate] = React.useState("");
  const [editCancelledDate, setEditCancelledDate] = React.useState("");
  const [editSource, setEditSource] = React.useState("");
  const [editExternalSubscriptionId, setEditExternalSubscriptionId] = React.useState("");
  const [editZohoOrganizationId, setEditZohoOrganizationId] = React.useState("");
  const [editCancellationReason, setEditCancellationReason] = React.useState("");
  const [editNotes, setEditNotes] = React.useState("");
  const [editIsRecurring, setEditIsRecurring] = React.useState(false);
  const [editAutoRenew, setEditAutoRenew] = React.useState(false);
  const [editDbaName, setEditDbaName] = React.useState("");
  const [editOrganizationName, setEditOrganizationName] = React.useState("");
  const [editPlan, setEditPlan] = React.useState("");
  const [overrideStatusDialogOpen, setOverrideStatusDialogOpen] = React.useState(false);
  const [overrideStatusValue, setOverrideStatusValue] = React.useState("");
  const [overridingStatus, setOverridingStatus] = React.useState(false);

  const fetchSubscription = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`/admin/subscriptions/${id}`);
      setSubscription(data);
      setEditAmount(data.amount ?? "");
      setEditPaymentSystem(data.payment_system ?? "");
      setEditPaymentMethodId(data.payment_method_id ?? "");
      setEditStartDate(toDateInputValue(data.start_date));
      setEditEndDate(toDateInputValue(data.end_date));
      setEditRenewalDate(toDateInputValue(data.renewal_date));
      setEditNextBillingDate(toDateInputValue(data.next_billing_date));
      setEditCancelledDate(toDateInputValue(data.cancelled_date));
      setEditSource(data.source ?? "");
      setEditExternalSubscriptionId(data.external_subscription_id ?? "");
      setEditZohoOrganizationId(data.zoho_organization_id ?? "");
      setEditCancellationReason(data.cancellation_reason ?? "");
      setEditNotes(data.notes ?? "");
      setEditIsRecurring(data.is_recurring === true);
      setEditAutoRenew(data.auto_renew === true);
      setEditDbaName(data.dba_name ?? "");
      setEditOrganizationName(data.organization_name ?? "");
      setEditPlan(data.plan ?? "");
    } catch (err) {
      console.error("Error fetching subscription:", err);
      setError(err.message || "Failed to fetch subscription");
    } finally {
      setLoading(false);
    }
  }, [id, api]);

  React.useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const fetchOrgContacts = React.useCallback(async () => {
    const orgId = subscription?.organization_id;
    if (!orgId || String(subscription?.source || "").toLowerCase() !== "squarespace") return;
    setLoadingContacts(true);
    try {
      const data = await api.get(`/admin/organizations/${orgId}/contacts`);
      setOrgContacts(Array.isArray(data) ? data : data?.contacts ?? []);
    } catch {
      setOrgContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  }, [subscription?.organization_id, subscription?.source, api]);

  React.useEffect(() => {
    if (subscription && String(subscription.source || "").toLowerCase() === "squarespace") {
      fetchOrgContacts();
    }
  }, [subscription?.id, subscription?.organization_id, subscription?.source, fetchOrgContacts]);

  const ssSubscriptionId = subscription?.meta_data?.squarespace_subscription_id;
  const fetchSsStatus = React.useCallback(async () => {
    const ssId = typeof ssSubscriptionId === "string" ? ssSubscriptionId.trim() : ssSubscriptionId != null ? String(ssSubscriptionId) : null;
    if (!ssId) return;
    setSsStatusLoading(true);
    setSsStatusError(null);
    try {
      const data = await api.get(`/admin/squarespace/subscriptions/${encodeURIComponent(ssId)}`);
      setSsStatus(data.data ?? data);
    } catch (err) {
      setSsStatus(null);
      setSsStatusError(err.message || "Failed to fetch Squarespace status");
    } finally {
      setSsStatusLoading(false);
    }
  }, [ssSubscriptionId, api]);

  const handleSyncFromSquarespace = React.useCallback(async () => {
    if (!subscription?.id || !ssSubscriptionId) return;
    setSyncFromSsLoading(true);
    setUpdateError(null);
    try {
      await api.post(`/admin/subscriptions/${subscription.id}/sync-from-squarespace`, {});
      await fetchSubscription();
      await fetchSsStatus();
    } catch (err) {
      setUpdateError(err.message || "Failed to sync from Squarespace");
    } finally {
      setSyncFromSsLoading(false);
      setSyncFromSsConfirmOpen(false);
    }
  }, [subscription?.id, ssSubscriptionId, api, fetchSubscription, fetchSsStatus]);

  React.useEffect(() => {
    if (subscription && String(subscription.source || "").toLowerCase() === "squarespace" && ssSubscriptionId) {
      fetchSsStatus();
    } else {
      setSsStatus(null);
      setSsStatusError(null);
    }
  }, [subscription?.id, subscription?.source, ssSubscriptionId, fetchSsStatus]);

  // Sync auto-renew toggle from SS status when available (source of truth for Squarespace subs)
  React.useEffect(() => {
    if (ssStatus != null && ssSubscriptionId) {
      const autoRenew = ssStatus.autoRenew ?? ssStatus.auto_renew;
      setEditAutoRenew(autoRenew === true);
    }
  }, [ssStatus, ssSubscriptionId]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const handleOpenMetaDataModal = () => {
    setEditMetaData(
      subscription?.meta_data != null ? JSON.stringify(subscription.meta_data, null, 2) : "{}"
    );
    setMetaDataError(null);
    setMetaDataModalOpen(true);
  };

  const handleSaveMetaData = async () => {
    if (!subscription) return;
    setMetaDataError(null);
    let parsed;
    try {
      parsed = editMetaData.trim() === "" ? null : JSON.parse(editMetaData);
    } catch {
      setMetaDataError("Invalid JSON");
      return;
    }
    setUpdatingMetaData(true);
    try {
      const updated = await api.patch(`/admin/subscriptions/${subscription.id}`, {
        meta_data: parsed,
      });
      setSubscription(updated);
      setMetaDataModalOpen(false);
    } catch (err) {
      setMetaDataError(err.message || "Failed to update meta_data");
    } finally {
      setUpdatingMetaData(false);
    }
  };

  const handleUpdateSubscription = async () => {
    if (!subscription) return;
    const amountNum = parseFloat(editAmount);
    if (editAmount.trim() === "" || isNaN(amountNum) || amountNum < 0) {
      setUpdateError("Amount is required and must be 0 or greater");
      return;
    }
    if (!editSource.trim()) {
      setUpdateError("Source is required");
      return;
    }
    setUpdating(true);
    setUpdateError(null);
    try {
      const updateData = {
        plan: editPlan || null,
        amount: editAmount !== "" ? parseFloat(editAmount) : null,
        payment_system: editPaymentSystem || null,
        payment_method_id: editPaymentMethodId || null,
        start_date: editStartDate ? new Date(editStartDate).toISOString() : null,
        end_date: editEndDate ? new Date(editEndDate).toISOString() : null,
        renewal_date: editRenewalDate ? new Date(editRenewalDate).toISOString() : null,
        next_billing_date: editNextBillingDate ? new Date(editNextBillingDate).toISOString() : null,
        cancelled_date: editCancelledDate ? new Date(editCancelledDate).toISOString() : null,
        source: editSource || null,
        external_subscription_id: editExternalSubscriptionId || null,
        zoho_organization_id: editZohoOrganizationId || null,
        cancellation_reason: editCancellationReason || null,
        notes: editNotes || null,
        is_recurring: editIsRecurring,
        auto_renew: editAutoRenew,
      };
      await api.patch(`/admin/subscriptions/${subscription.id}`, updateData);

      if (subscription.organization_id) {
        await api.patch(`/admin/organizations/${subscription.organization_id}`, {
          dba_name: editDbaName || null,
          organization_name: editOrganizationName || null,
        });
      }

      await fetchSubscription();
    } catch (err) {
      setUpdateError(err.message || "Failed to update subscription");
    } finally {
      setUpdating(false);
    }
  };

  const handleOverrideStatusOpen = () => {
    setOverrideStatusValue((subscription?.status || "pending").toLowerCase());
    setOverrideStatusDialogOpen(true);
  };
  const handleOverrideStatusClose = () => setOverrideStatusDialogOpen(false);
  const handleOverrideStatusConfirm = async () => {
    if (!subscription) return;
    setOverridingStatus(true);
    setUpdateError(null);
    try {
      await api.patch(`/admin/subscriptions/${subscription.id}`, {
        status: overrideStatusValue,
      });
      setOverrideStatusDialogOpen(false);
      await fetchSubscription();
    } catch (err) {
      setUpdateError(err.message || "Failed to override status");
    } finally {
      setOverridingStatus(false);
    }
  };

  const handleCancelClick = () => setCancelDialogOpen(true);
  const handleCancelClose = () => setCancelDialogOpen(false);

  const handleReactivateClick = () => setReactivateDialogOpen(true);
  const handleReactivateClose = () => setReactivateDialogOpen(false);

  const handleReactivateConfirm = async () => {
    if (!subscription) return;
    setReactivating(true);
    try {
      await api.patch(`/admin/subscriptions/${subscription.id}`, {
        status: "active",
        cancelled_date: null,
      });
      setReactivateDialogOpen(false);
      await fetchSubscription();
    } catch (err) {
      setUpdateError(err.message || "Failed to reactivate subscription");
    } finally {
      setReactivating(false);
    }
  };

  const isLocked = (subscription?.status || '').toLowerCase() === 'locked';
  const isProcessing = (subscription?.status || '').toLowerCase() === 'processing';

  const handleAutoRenewButtonClick = (newValue) => {
    if (newValue === editAutoRenew) return;
    setAutoRenewPendingValue(newValue);
    setAutoRenewConfirmOpen(true);
  };

  const handleAutoRenewConfirm = async () => {
    if (!subscription || autoRenewPendingValue == null) return;
    setUpdatingAutoRenew(true);
    setUpdateError(null);
    try {
      await api.put(`/admin/subscriptions/${subscription.id}`, {
        auto_renew: autoRenewPendingValue,
      });
      setEditAutoRenew(autoRenewPendingValue);
      setAutoRenewConfirmOpen(false);
      setAutoRenewPendingValue(null);
      await fetchSubscription();
      setTimeout(() => fetchSsStatus(), 3000);
    } catch (err) {
      setUpdateError(err.message || "Failed to update auto-renew");
    } finally {
      setUpdatingAutoRenew(false);
    }
  };

  const handleCreateOrder = async () => {
    const customerId = (createOrderFormData.customerId || "").trim();
    const pricingId = (createOrderFormData.pricingId || "").trim();
    const title = (createOrderFormData.title || "").trim();
    if (!customerId) {
      setCreateOrderError("Customer ID is required");
      return;
    }
    if (!pricingId) {
      setCreateOrderError("Pricing ID is required");
      return;
    }
    if (!title) {
      setCreateOrderError("Website title is required");
      return;
    }
    setCreatingOrder(true);
    setCreateOrderError(null);
    setCreateOrderResponse(null);
    try {
      const newOrderItemWebSite = { pricingId, title };
      if (createOrderFormData.domain?.trim()) {
        newOrderItemWebSite.domains = [createOrderFormData.domain.trim()];
      }
      const requestBody = {
        customerId,
        newOrderItemWebSite,
        ...(subscription?.id && { brytebridge_subscription_id: subscription.id }),
      };
      const data = await api.post("/admin/squarespace/orders", requestBody);
      setCreateOrderResponse(data);
      await fetchSubscription();
    } catch (err) {
      setCreateOrderError(err.message || "Failed to create order");
    } finally {
      setCreatingOrder(false);
    }
  };

  const handleOpenCreateOrderDialog = () => {
    const meta = subscription?.meta_data;
    const ssCustomerId = meta?.squarespace_customer_id;
    const ssPricingId = subscription?.plan_details?.meta_data?.squarespace_pricing_id;
    setCreateOrderFormData((prev) => ({
      ...prev,
      customerId: ssCustomerId ? String(ssCustomerId) : prev.customerId,
      pricingId: ssPricingId ? String(ssPricingId) : prev.pricingId,
    }));
    setCreateOrderError(null);
    setCreateOrderResponse(null);
    setCreateOrderDialogOpen(true);
  };

  const handleFindSubscription = async () => {
    if (!subscription?.id) return;
    const meta = subscription.meta_data ?? {};
    const orderId = meta.squarespace_order_id;
    const customerId = meta.squarespace_customer_id;
    if (!orderId || !customerId) {
      setFindSubscriptionError("Need squarespace_order_id and squarespace_customer_id in meta_data");
      return;
    }
    setFindSubscriptionLoading(true);
    setFindSubscriptionError(null);
    try {
      await api.post(`/admin/subscriptions/${subscription.id}/link-squarespace-order`, {
        orderId: String(orderId),
        customerId: String(customerId),
      });
      await fetchSubscription();
    } catch (err) {
      setFindSubscriptionError(err.message || "Failed to find subscription");
    } finally {
      setFindSubscriptionLoading(false);
    }
  };

  const handleLinkSquarespaceCustomer = async () => {
    if (!subscription?.id || !provisioningContactId?.trim()) return;
    setProvisioningLinkLoading(true);
    setProvisioningLinkError(null);
    setProvisioningLinkErrorDetail(null);
    try {
      await api.post(`/admin/subscriptions/${subscription.id}/link-squarespace-customer`, {
        contactId: provisioningContactId.trim(),
      });
      await fetchSubscription();
      await fetchOrgContacts();
    } catch (err) {
      setProvisioningLinkError(err.message || "Failed to link Squarespace customer");
      setProvisioningLinkErrorDetail("Check the browser console or API logs for details.");
    } finally {
      setProvisioningLinkLoading(false);
    }
  };

  const handleCreateContact = async () => {
    if (!subscription?.organization_id) return;
    const { first_name, last_name, email } = createContactFormData;
    if (!first_name?.trim() || !last_name?.trim() || !email?.trim()) {
      setProvisioningLinkError("First name, last name, and email are required");
      setProvisioningLinkErrorDetail(null);
      return;
    }
    setCreatingContact(true);
    setProvisioningLinkError(null);
    setProvisioningLinkErrorDetail(null);
    try {
      const data = await api.post("/admin/contacts", {
        organization_id: subscription.organization_id,
        first_name: first_name.trim(),
        last_name: last_name.trim(),
        email: email.trim(),
      });
      const newContact = Array.isArray(data) ? data[0] : data;
      setCreateContactFormData({ first_name: "", last_name: "", email: "" });
      await fetchOrgContacts();
      if (newContact?.id) {
        setProvisioningContactId(newContact.id);
      }
    } catch (err) {
      setProvisioningLinkError(err.message || "Failed to create contact");
      setProvisioningLinkErrorDetail(null);
    } finally {
      setCreatingContact(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!subscription) return;
    setCancelling(true);
    try {
      await api.patch(`/admin/subscriptions/${subscription.id}`, {
        status: "cancelled",
        cancelled_date: new Date().toISOString(),
      });
      setCancelDialogOpen(false);
      await fetchSubscription();
    } catch (err) {
      setUpdateError(err.message || "Failed to cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
          <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Admin
                </Typography>
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard/subscriptions")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Subscriptions
                </Typography>
              </Breadcrumbs>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Box>
                <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                  Subscription Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Edit subscription information
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                {subscription?.organization_id && (
                  <Button
                    variant="outlined"
                    startIcon={<BusinessIcon />}
                    onClick={() => navigate(`/dashboard/organizations/${subscription.organization_id}`)}
                  >
                    Go to Org
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<MenuBookIcon />}
                  onClick={() => navigate("/dashboard/docs?topic=subscriptions")}
                >
                  How subscriptions work
                </Button>
                {subscription && (
                  <Button
                    variant="outlined"
                    startIcon={<CodeIcon />}
                    onClick={() => setJsonModalOpen(true)}
                  >
                    View JSON
                  </Button>
                )}
              </Box>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {updateError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUpdateError(null)}>
                {updateError}
              </Alert>
            )}

            {loading ? (
              <Card>
                <CardContent>
                  <Skeleton height={40} width={200} />
                  <Box sx={{ mt: 2 }}>
                    <Skeleton height={20} width={150} />
                    <Skeleton height={20} width={200} />
                    <Skeleton height={20} width={180} />
                  </Box>
                </CardContent>
              </Card>
            ) : subscription ? (
              <>
                {(subscription?.status || '').toLowerCase() === 'locked' && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This subscription is locked to prevent changes on the Portal site. Users cannot cancel, reactivate, or change auto-renew.
                  </Alert>
                )}
                {(subscription?.status || '').toLowerCase() === 'processing' && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Syncing with Squarespace… Status will update when the sync completes.
                  </Alert>
                )}
                <Card sx={{ mb: 2 }}>
                  <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                      <Tab label="Details" />
                      <Tab label="Provisioning" />
                    </Tabs>
                  </Box>
                  <TabPanel value={tabValue} index={0}>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4, alignItems: "flex-start" }}>
                        <Box sx={{ flex: "1 1 50%", minWidth: 0, width: "100%" }}>
                          {/* Financial Information Section */}
                          <Card sx={{ mb: 0 }}>
                            <CardContent sx={{ pb: 0, "&:last-child": { pb: 0 } }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                                Financial Information
                              </Typography>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
                                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", width: "100%" }}>
                                  <Box sx={{ flex: "1 1 calc(33.333% - 16px)", minWidth: 150 }}>
                                    <TextField
                                      fullWidth
                                      required
                                      label="Amount (amount)"
                                      type="number"
                                      value={editAmount}
                                      onChange={(e) => { setEditAmount(e.target.value); setUpdateError(null); }}
                                      disabled={updating || isLocked || isProcessing}
                                      InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: "text.secondary" }}>$</Typography> }}
                                    />
                                  </Box>
                                  <Box sx={{ flex: "1 1 calc(33.333% - 16px)", minWidth: 150 }}>
                                    <FormControl fullWidth required>
                                      <InputLabel>Source (source)</InputLabel>
                                      <Select
                                        value={editSource || ""}
                                        label="Source (source)"
                                        onChange={(e) => {
                                          setEditSource(e.target.value);
                                          setUpdateError(null);
                                        }}
                                        disabled={updating || isLocked || isProcessing}
                                      >
                                        <MenuItem value="">None</MenuItem>
                                        {[...SUBSCRIPTION_SOURCES]
                                          .concat(editSource && !SUBSCRIPTION_SOURCES.includes(editSource) ? [editSource] : [])
                                          .map((s) => (
                                            <MenuItem key={s} value={s}>
                                              {s}
                                            </MenuItem>
                                          ))}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                  <Box sx={{ flex: "1 1 calc(33.333% - 16px)", minWidth: 150 }}>
                                    <TextField
                                      fullWidth
                                      label="External Subscription ID"
                                      value={editExternalSubscriptionId}
                                      onChange={(e) => { setEditExternalSubscriptionId(e.target.value); setUpdateError(null); }}
                                      disabled={updating || isLocked || isProcessing}
                                    />
                                  </Box>
                                </Box>
                                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", width: "100%" }}>
                                  <Box sx={{ flex: "1 1 calc(33.333% - 16px)", minWidth: 150 }}>
                                    <TextField
                                      fullWidth
                                      label="Payment Method ID (payment_method_id)"
                                      value={editPaymentMethodId}
                                      onChange={(e) => { setEditPaymentMethodId(e.target.value); setUpdateError(null); }}
                                      disabled={updating || isLocked || isProcessing}
                                    />
                                  </Box>
                                  <Box sx={{ flex: "1 1 calc(33.333% - 16px)", minWidth: 150 }}>
                                    <TextField
                                      fullWidth
                                      label="Payment System (payment_system)"
                                      value={editPaymentSystem}
                                      onChange={(e) => { setEditPaymentSystem(e.target.value); setUpdateError(null); }}
                                      disabled={updating || isLocked || isProcessing}
                                    />
                                  </Box>
                                  <Box sx={{ flex: "1 1 calc(33.333% - 16px)", minWidth: 150 }}>
                                    <TextField
                                      fullWidth
                                      label="Zoho Organization ID"
                                      value={editZohoOrganizationId}
                                      onChange={(e) => { setEditZohoOrganizationId(e.target.value); setUpdateError(null); }}
                                      disabled={updating || isLocked || isProcessing}
                                    />
                                  </Box>
                                </Box>

                                {/* Base Status - read-only with manual override */}
                                <Card
                                  sx={{
                                    mt: 1,
                                    mb: 1,
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                  }}
                                >
                                  <CardContent sx={{ pt: 1, "&:last-child": { pb: 1 } }}>
                                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                                      Base Status
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                      BryteBridge subscription status. For Squarespace subscriptions, this is normally set when the order is linked.
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                      <Chip
                                        label={
                                          subscription?.status
                                            ? (subscription.status.toLowerCase() === "processing"
                                                ? "Processing"
                                                : subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1).toLowerCase())
                                            : "—"
                                        }
                                        size="small"
                                        color={
                                          !subscription?.status
                                            ? "default"
                                            : subscription.status.toLowerCase() === "active"
                                              ? "success"
                                              : subscription.status.toLowerCase() === "processing"
                                                ? "info"
                                                : ["cancelled", "canceled", "expired"].includes((subscription?.status || "").toLowerCase())
                                                  ? "error"
                                                  : subscription.status.toLowerCase() === "paused"
                                                    ? "warning"
                                                    : "default"
                                        }
                                      />
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleOverrideStatusOpen}
                                        disabled={updating}
                                      >
                                        Override status
                                      </Button>
                                    </Box>
                                  </CardContent>
                                </Card>

                                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", width: "100%" }}>
                                  <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 200 }}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={editIsRecurring}
                                          onChange={(e) => { setEditIsRecurring(e.target.checked); setUpdateError(null); }}
                                          disabled={updating || isLocked || isProcessing}
                                        />
                                      }
                                      label="Recurring (is_recurring)"
                                    />
                                  </Box>
                                  {String(subscription.source || "").toLowerCase() !== "squarespace" && (
                                    <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 200 }}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={editAutoRenew}
                                            onChange={(e) => { setEditAutoRenew(e.target.checked); setUpdateError(null); }}
                                            disabled={updating || isLocked || isProcessing}
                                          />
                                        }
                                        label="Auto Renew (auto_renew)"
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>

                          {/* Important Dates Section */}
                          <Card sx={{ mb: 0 }}>
                            <CardContent sx={{ pt: 1, pb: 0, "&:last-child": { pb: 0 } }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                                Important Dates
                              </Typography>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
                                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", width: "100%" }}>
                                  <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 200 }}>
                                    <TextField
                                      fullWidth
                                      required
                                      label="Start Date"
                                      type="datetime-local"
                                      value={editStartDate}
                                      onChange={(e) => { setEditStartDate(e.target.value); setUpdateError(null); }}
                                      disabled={updating || isLocked || isProcessing}
                                      InputLabelProps={{ shrink: true }}
                                    />
                                  </Box>
                                  <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 200 }}>
                                    <TextField
                                      fullWidth
                                      required
                                      label="End Date"
                                      type="datetime-local"
                                      value={editEndDate}
                                      onChange={(e) => { setEditEndDate(e.target.value); setUpdateError(null); }}
                                      disabled={updating || isLocked || isProcessing}
                                      InputLabelProps={{ shrink: true }}
                                    />
                                  </Box>
                                </Box>
                                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", width: "100%" }}>
                                  <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 200 }}>
                                    <TextField
                                      fullWidth
                                      label="Renewal Date"
                                      type="datetime-local"
                                      value={editRenewalDate}
                                      onChange={(e) => { setEditRenewalDate(e.target.value); setUpdateError(null); }}
                                      disabled={updating || isLocked || isProcessing}
                                      InputLabelProps={{ shrink: true }}
                                    />
                                  </Box>
                                  <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 200 }}>
                                    <TextField
                                      fullWidth
                                      label="Next Billing Date"
                                      type="datetime-local"
                                      value={editNextBillingDate}
                                      onChange={(e) => { setEditNextBillingDate(e.target.value); setUpdateError(null); }}
                                      disabled={updating || isLocked || isProcessing}
                                      InputLabelProps={{ shrink: true }}
                                    />
                                  </Box>
                                </Box>
                                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", width: "100%" }}>
                                  <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 200 }}>
                                    <TextField
                                      fullWidth
                                      label="Cancelled Date"
                                      type="datetime-local"
                                      value={editCancelledDate}
                                      onChange={(e) => { setEditCancelledDate(e.target.value); setUpdateError(null); }}
                                      disabled={updating || isLocked || isProcessing}
                                      InputLabelProps={{ shrink: true }}
                                    />
                                  </Box>
                                  <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 200 }}>
                                    <TextField
                                      fullWidth
                                      label="Cancellation Reason (cancellation_reason)"
                                      value={editCancellationReason}
                                      onChange={(e) => { setEditCancellationReason(e.target.value); setUpdateError(null); }}
                                      disabled={updating || isLocked || isProcessing}
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>

                          {/* Additional Information Section */}
                          <Card sx={{ mb: 2 }}>
                            <CardContent sx={{ pt: 1 }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                                Additional Information
                              </Typography>
                              <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
                                <TextField
                                  fullWidth
                                  label="Notes"
                                  value={editNotes}
                                  onChange={(e) => { setEditNotes(e.target.value); setUpdateError(null); }}
                                  disabled={updating || isLocked}
                                  multiline
                                  rows={3}
                                />
                                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", width: "100%" }}>
                                  {subscription.organization_id && (
                                    <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 150 }}>
                                      <TextField
                                        fullWidth
                                        label="Organization Name (organization_name)"
                                        value={editOrganizationName}
                                        onChange={(e) => { setEditOrganizationName(e.target.value); setUpdateError(null); }}
                                        disabled={updating || isLocked || isProcessing}
                                        placeholder="Organization name"
                                      />
                                    </Box>
                                  )}
                                  <Box sx={{ flex: subscription.organization_id ? "1 1 calc(50% - 12px)" : "1 1 100%", minWidth: 150 }}>
                                    <TextField
                                      fullWidth
                                      label="DBA Name (dba_name)"
                                      value={editDbaName}
                                      onChange={(e) => { setEditDbaName(e.target.value); setUpdateError(null); }}
                                      disabled={updating || isLocked || isProcessing}
                                      placeholder="DBA name"
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>

                          {/* Plan */}
                          <Card sx={{ mb: 2 }}>
                            <CardContent sx={{ pt: 1 }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                                Plan
                              </Typography>
                              <TextField
                                fullWidth
                                required
                                label="Plan"
                                value={editPlan || ""}
                                onChange={(e) => { setEditPlan(e.target.value); setUpdateError(null); }}
                                disabled={updating || isLocked}
                                placeholder="Subscription plan ID"
                                helperText="Subscription plan ID (foreign key to subscription_plans) — required"
                              />
                            </CardContent>
                          </Card>

                          <Box sx={{ mt: 3, mb: 3 }}>
                            <Button
                              variant="contained"
                              startIcon={updating ? <CircularProgress size={16} /> : <EditIcon />}
                              onClick={handleUpdateSubscription}
                              disabled={
                                updating ||
                                isLocked ||
                                !editAmount.trim() ||
                                !editSource.trim() ||
                                !editPlan.trim() ||
                                !editStartDate?.trim() ||
                                !editEndDate?.trim()
                              }
                            >
                              {updating ? "Updating..." : "Update Subscription"}
                            </Button>
                          </Box>
                        </Box>
                        <Box sx={{ flex: "1 1 50%", width: "100%", minWidth: 0 }}>
                          {/* Current Squarespace Status - live from SS API */}
                          {String(subscription.source || "").toLowerCase() === "squarespace" && (
                            <Box
                              sx={{
                                p: 2,
                                mb: 2,
                                borderRadius: 1,
                                border: 1,
                                borderColor: "divider",
                                bgcolor: "background.paper",
                              }}
                            >
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                                  Current Squarespace Status
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  {ssSubscriptionId && (
                                    <>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={ssStatusLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                                        onClick={fetchSsStatus}
                                        disabled={ssStatusLoading}
                                      >
                                        Refresh
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={syncFromSsLoading ? <CircularProgress size={16} /> : <SyncIcon />}
                                        onClick={() => setSyncFromSsConfirmOpen(true)}
                                        disabled={syncFromSsLoading}
                                      >
                                        Sync from Squarespace
                                      </Button>
                                    </>
                                  )}
                                  {ssStatus && (
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<CodeIcon />}
                                      onClick={() => setSsJsonModalOpen(true)}
                                    >
                                      View JSON
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                              {ssSubscriptionId ? (
                                <>
                                  <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block" sx={{ mb: 0.5 }}>
                                    Squarespace subscription ID
                                  </Typography>
                                  <Link
                                    component="button"
                                    variant="body2"
                                    onClick={() => navigate(`/dashboard/squarespace/subscriptions/${ssSubscriptionId}`)}
                                    sx={{
                                      display: "block",
                                      fontFamily: "monospace",
                                      fontSize: "0.75rem",
                                      bgcolor: "action.hover",
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 0.5,
                                      mb: 1,
                                      wordBreak: "break-all",
                                      textAlign: "left",
                                      textDecoration: "none",
                                      "&:hover": { textDecoration: "underline" },
                                    }}
                                  >
                                    {String(ssSubscriptionId)}
                                  </Link>
                                  {ssStatusLoading ? (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      <CircularProgress size={18} />
                                      <Typography variant="body2" color="text.secondary">Fetching from Squarespace…</Typography>
                                    </Box>
                                  ) : ssStatusError ? (
                                    <Alert severity="error" sx={{ py: 0.5 }}>{ssStatusError}</Alert>
                                  ) : ssStatus ? (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {(ssStatus.resoldStatus || ssStatus.status) === "ACTIVE" && <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />}
                                        {((ssStatus.resoldStatus || ssStatus.status) === "CANCELLED" || (ssStatus.resoldStatus || ssStatus.status) === "EXPIRED") && (
                                          <CancelIcon color="error" sx={{ fontSize: 18 }} />
                                        )}
                                        <Typography variant="body2">
                                          {(ssStatus.resoldStatus || ssStatus.status || "—").charAt(0).toUpperCase() +
                                            (ssStatus.resoldStatus || ssStatus.status || "").slice(1).toLowerCase()}
                                        </Typography>
                                      </Box>
                                      <Typography variant="caption" color="text.secondary">
                                        Auto renew: {ssStatus.autoRenew === true ? "Yes" : ssStatus.autoRenew === false ? "No" : "—"}
                                        {(ssStatus.expiresOn || ssStatus.expires_on) && ` · Expires: ${formatDate(ssStatus.expiresOn || ssStatus.expires_on)}`}
                                      </Typography>
                                      {(subscription.process_status && subscription.process_status !== "na") && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                          Sync: {subscription.process_status}
                                          {subscription.process_status === "failed" && subscription.meta_data?.process_error && (
                                            <Box component="span" sx={{ color: "error.main", display: "block", fontFamily: "monospace" }}>
                                              {subscription.meta_data.process_error}
                                            </Box>
                                          )}
                                        </Typography>
                                      )}
                                      {!isLocked && !isProcessing && (
                                        <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                                          {(ssStatus.resoldStatus || ssStatus.status) === "ACTIVE" && (
                                            <Button
                                              variant="outlined"
                                              color="error"
                                              size="small"
                                              startIcon={<CancelIcon />}
                                              onClick={handleCancelClick}
                                              disabled={updating || cancelling}
                                            >
                                              Cancel Subscription
                                            </Button>
                                          )}
                                          {["CANCELLED", "EXPIRED"].includes(ssStatus.resoldStatus || ssStatus.status) && (
                                            <Button
                                              variant="outlined"
                                              color="primary"
                                              size="small"
                                              startIcon={<PlayArrowIcon />}
                                              onClick={handleReactivateClick}
                                              disabled={updating || reactivating}
                                            >
                                              Reactivate Subscription
                                            </Button>
                                          )}
                                        </Box>
                                      )}
                                    </Box>
                                  ) : null}
                                  {!isLocked && !isProcessing && (
                                    <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, border: 1, borderColor: "divider", bgcolor: "action.hover" }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block" sx={{ mb: 0.5 }}>
                                        Change Renewal
                                      </Typography>
                                      {ssStatusLoading ? (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                          <CircularProgress size={14} />
                                          <Typography variant="body2" color="text.secondary">Loading from Squarespace…</Typography>
                                        </Box>
                                      ) : updatingAutoRenew ? (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                          <CircularProgress size={14} />
                                          <Typography variant="body2" color="text.secondary">Updating…</Typography>
                                        </Box>
                                      ) : (
                                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                                          <Button
                                            variant={editAutoRenew ? "contained" : "outlined"}
                                            size="small"
                                            onClick={() => handleAutoRenewButtonClick(true)}
                                            disabled={updating || ssStatusLoading}
                                          >
                                            On
                                          </Button>
                                          <Button
                                            variant={!editAutoRenew ? "contained" : "outlined"}
                                            size="small"
                                            onClick={() => handleAutoRenewButtonClick(false)}
                                            disabled={updating || ssStatusLoading}
                                          >
                                            Off
                                          </Button>
                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                          (from Squarespace)
                                        </Typography>
                                      </Box>
                                    )}
                                    </Box>
                                  )}
                                </>
                              ) : (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    No Squarespace subscription yet. Set up the customer and create the order.
                                  </Typography>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block" sx={{ mb: 1 }}>
                                      1. Customer
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<PersonSearchIcon />}
                                        onClick={() => navigate("/dashboard/squarespace/customers")}
                                      >
                                        Select Customer
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => navigate("/dashboard/squarespace/customers/new")}
                                      >
                                        Create Customer
                                      </Button>
                                      {subscription?.meta_data?.squarespace_customer_id && (
                                        <Typography variant="caption" color="success.main" sx={{ alignSelf: "center", ml: 1 }}>
                                          Customer ID in meta_data
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block" sx={{ mb: 1 }}>
                                      2. Create Order
                                    </Typography>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      startIcon={<ShoppingCartIcon />}
                                      onClick={handleOpenCreateOrderDialog}
                                    >
                                      Create Order
                                    </Button>
                                  </Box>
                                  {subscription?.meta_data?.squarespace_order_id &&
                                    subscription?.meta_data?.squarespace_customer_id && (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block" sx={{ mb: 1 }}>
                                        3. Find Subscription (SS creates it async)
                                      </Typography>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={
                                          findSubscriptionLoading ? (
                                            <CircularProgress size={16} />
                                          ) : (
                                            <RefreshIcon />
                                          )
                                        }
                                        onClick={handleFindSubscription}
                                        disabled={findSubscriptionLoading}
                                      >
                                        {findSubscriptionLoading ? "Searching…" : "Find Subscription"}
                                      </Button>
                                      {findSubscriptionError && (
                                        <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
                                          {findSubscriptionError}
                                        </Alert>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          )}

                          {/* Provider-specific controls: non-Squarespace */}
                          {String(subscription.source || "").toLowerCase() !== "squarespace" && (
                            <Box
                              sx={{
                                p: 2,
                                mb: 2,
                                borderRadius: 1,
                                border: 1,
                                borderColor: "divider",
                                bgcolor: "grey.50",
                              }}
                            >
                              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                                Provider Controls
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                No controls set for this vendor or provider type.
                              </Typography>
                            </Box>
                          )}

                          <Box sx={{ p: 3, borderRadius: 1, bgcolor: "grey.50", border: 1, borderColor: "divider" }}>
                            <Typography variant="h6" gutterBottom fontWeight="bold">
                              Metadata
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                                  Created At
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.25 }}>
                                  {formatDate(subscription.created_at)}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                                  Updated At
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.25 }}>
                                  {formatDate(subscription.updated_at)}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                                  Created By
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.25 }}>
                                  {subscription.created_by ?? "N/A"}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                                  Cancelled By
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.25 }}>
                                  {subscription.cancelled_by ?? "N/A"}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                                  Plan
                                </Typography>
                                {subscription.plan ? (
                                  <Link
                                    component="button"
                                    variant="body2"
                                    onClick={() => navigate(`/dashboard/subscription-plans/${subscription.plan}`)}
                                    sx={{ mt: 0.25, display: "block", textAlign: "left" }}
                                  >
                                    {subscription.plan}
                                  </Link>
                                ) : (
                                  <Typography variant="body2" sx={{ mt: 0.25 }}>
                                    N/A
                                  </Typography>
                                )}
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                                  Plan Legacy
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.25 }}>
                                  {subscription.plan_legacy ?? "N/A"}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                                  Organization Name
                                </Typography>
                                {subscription.organization_id ? (
                                  <Link
                                    component="button"
                                    variant="body2"
                                    onClick={() => navigate(`/dashboard/organizations/${subscription.organization_id}`)}
                                    sx={{ mt: 0.25, display: "block", textAlign: "left" }}
                                  >
                                    {subscription.organization_name ?? "View"}
                                  </Link>
                                ) : (
                                  <Typography variant="body2" sx={{ mt: 0.25 }}>
                                    {subscription.organization_name ?? "N/A"}
                                  </Typography>
                                )}
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                                  DBA Name
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.25 }}>
                                  {subscription.dba_name ?? "N/A"}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                                  Organization ID
                                </Typography>
                                {subscription.organization_id ? (
                                  <Link
                                    component="button"
                                    variant="body2"
                                    onClick={() => navigate(`/dashboard/organizations/${subscription.organization_id}`)}
                                    sx={{ mt: 0.25, display: "block", textAlign: "left" }}
                                  >
                                    {subscription.organization_id}
                                  </Link>
                                ) : (
                                  <Typography variant="body2" sx={{ mt: 0.25 }}>
                                    N/A
                                  </Typography>
                                )}
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                                  Meta Data
                                </Typography>
                                <Box
                                  component="pre"
                                  sx={{
                                    mt: 0.25,
                                    p: 1.5,
                                    borderRadius: 1,
                                    bgcolor: "grey.100",
                                    fontSize: "0.75rem",
                                    fontFamily: "monospace",
                                    overflow: "auto",
                                    maxHeight: 200,
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {subscription.meta_data != null
                                    ? JSON.stringify(subscription.meta_data, null, 2)
                                    : "—"}
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.5, flexWrap: "wrap" }}>
                                  <Typography
                                    component="button"
                                    variant="body2"
                                    color="primary"
                                    onClick={handleOpenMetaDataModal}
                                    sx={{
                                      border: "none",
                                      background: "none",
                                      cursor: "pointer",
                                      textDecoration: "underline",
                                      "&:hover": { textDecoration: "none" },
                                    }}
                                  >
                                    Edit meta_data
                                  </Typography>
                                  {(() => {
                                    const meta = parseSubscriptionMetaData(subscription?.meta_data);
                                    return SUBSCRIPTION_META_SS_ADMIN_LINKS.map((def) => {
                                      const ssId = metaDataIdString(meta, def.key);
                                      if (!ssId) return null;
                                      return (
                                        <React.Fragment key={def.key}>
                                          <Typography variant="body2" color="text.secondary">
                                            |
                                          </Typography>
                                          <Link
                                            component="button"
                                            variant="body2"
                                            onClick={() => navigate(def.to(ssId))}
                                            title={`${def.label}: ${ssId}`}
                                            sx={{
                                              border: "none",
                                              background: "none",
                                              cursor: "pointer",
                                              textDecoration: "underline",
                                              "&:hover": { textDecoration: "none" },
                                            }}
                                          >
                                            {def.label}
                                          </Link>
                                        </React.Fragment>
                                      );
                                    });
                                  })()}
                                </Box>
                              </Box>
                              {subscription.source && String(subscription.source).toLowerCase() === "squarespace" && (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                    Manually complete provisioning
                                  </Typography>
                                  <Typography variant="body2" component="div" sx={{ "& ol": { pl: 2, my: 0.5 }, "& li": { mb: 0.75 } }}>
                                    Squarespace provisioning happens in two stages. You may need to complete both and then sync the data back here.
                                    <ol>
                                      <li>
                                        <strong>Create the customer.</strong> Use{" "}
                                        <Link
                                          component="button"
                                          variant="body2"
                                          onClick={() => navigate("/dashboard/squarespace/customers")}
                                          sx={{
                                            border: "none",
                                            background: "none",
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            "&:hover": { textDecoration: "none" },
                                            p: 0,
                                            font: "inherit",
                                          }}
                                        >
                                          Squarespace → Customers
                                        </Link>
                                        {" "}to create or find the customer. Use the{" "}
                                        <Box component="code" sx={{ fontFamily: "monospace", fontSize: "0.85em", bgcolor: "action.hover", px: 0.5, py: 0.25, borderRadius: 0.5 }}>
                                          squarespace_customer_id
                                        </Box>
                                        {" "}from meta_data above if it already exists.
                                      </li>
                                      <li>
                                        <strong>Create a subscription for that customer.</strong> Use{" "}
                                        <Link
                                          component="button"
                                          variant="body2"
                                          onClick={() => navigate("/dashboard/squarespace/orders")}
                                          sx={{
                                            border: "none",
                                            background: "none",
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            "&:hover": { textDecoration: "none" },
                                            p: 0,
                                            font: "inherit",
                                          }}
                                        >
                                          Squarespace → Orders
                                        </Link>
                                        {" "}or{" "}
                                        <Link
                                          component="button"
                                          variant="body2"
                                          onClick={() => navigate("/dashboard/squarespace/process-order")}
                                          sx={{
                                            border: "none",
                                            background: "none",
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            "&:hover": { textDecoration: "none" },
                                            p: 0,
                                            font: "inherit",
                                          }}
                                        >
                                          Submit Order
                                        </Link>
                                        {" "}to create the order and subscription. Look up the order by{" "}
                                        <Box component="code" sx={{ fontFamily: "monospace", fontSize: "0.85em", bgcolor: "action.hover", px: 0.5, py: 0.25, borderRadius: 0.5 }}>
                                          squarespace_order_id
                                        </Box>
                                        {" "}in{" "}
                                        <Link
                                          component="button"
                                          variant="body2"
                                          onClick={() => navigate("/dashboard/squarespace/subscriptions")}
                                          sx={{
                                            border: "none",
                                            background: "none",
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            "&:hover": { textDecoration: "none" },
                                            p: 0,
                                            font: "inherit",
                                          }}
                                        >
                                          Squarespace → Subscriptions
                                        </Link>
                                        {" "}to find the subscription ID.
                                      </li>
                                      <li>
                                        <strong>Update this subscription with the IDs.</strong> Use Edit meta_data above to add or correct{" "}
                                        <Box component="code" sx={{ fontFamily: "monospace", fontSize: "0.85em", bgcolor: "action.hover", px: 0.5, py: 0.25, borderRadius: 0.5 }}>
                                          squarespace_customer_id
                                        </Box>
                                        {", "}
                                        <Box component="code" sx={{ fontFamily: "monospace", fontSize: "0.85em", bgcolor: "action.hover", px: 0.5, py: 0.25, borderRadius: 0.5 }}>
                                          squarespace_order_id
                                        </Box>
                                        {", and "}
                                        <Box component="code" sx={{ fontFamily: "monospace", fontSize: "0.85em", bgcolor: "action.hover", px: 0.5, py: 0.25, borderRadius: 0.5 }}>
                                          squarespace_subscription_id
                                        </Box>
                                        {". "}
                                        The subscription ID is required for cancellation from the portal.
                                      </li>
                                    </ol>
                                  </Typography>
                                </Alert>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                  </TabPanel>
                  <TabPanel value={tabValue} index={1}>
                    {String(subscription?.source || "").toLowerCase() !== "squarespace" ? (
                      <Typography color="text.secondary">
                        Provisioning status applies only to Squarespace subscriptions.
                      </Typography>
                    ) : !subscription?.organization_id ? (
                      <Alert severity="info">
                        Link an organization to this subscription first to check provisioning status.
                      </Alert>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={handleOpenMetaDataModal}
                            sx={{ textDecoration: "underline", "&:hover": { textDecoration: "none" } }}
                          >
                            Edit subscription meta_data
                          </Link>
                        </Box>
                        {(() => {
                          const raw = subscription?.meta_data;
                          const subMeta = raw != null && typeof raw === "object" && !Array.isArray(raw)
                            ? raw
                            : typeof raw === "string"
                              ? (() => { try { const p = JSON.parse(raw); return p && typeof p === "object" ? p : {}; } catch { return {}; } })()
                              : {};
                          const hasSubCustomerId = !!(
                            (typeof subMeta.squarespace_customer_id === "string" && subMeta.squarespace_customer_id.trim()) ||
                            (typeof subMeta.squarespace_customer_id === "number")
                          );
                          const contactWithSsId = orgContacts.find((c) => {
                            let m = c.meta_data;
                            if (typeof m === "string") {
                              try { m = JSON.parse(m); } catch { m = null; }
                            }
                            const v = m && typeof m === "object" && !Array.isArray(m) ? m.squarespace_customer_id : null;
                            return v != null && (typeof v === "string" ? v.trim() : true);
                          });
                          const hasContactWithSsId = !!contactWithSsId;
                          const hasOrderId = !!(typeof subMeta.squarespace_order_id === "string" && subMeta.squarespace_order_id.trim()) ||
                            (typeof subMeta.squarespace_order_id === "number");
                          const hasSubId = !!(typeof subMeta.squarespace_subscription_id === "string" && subMeta.squarespace_subscription_id.trim()) ||
                            (typeof subMeta.squarespace_subscription_id === "number");

                          const StatusIcon = ({ ok }) => ok
                            ? <CheckCircleIcon color="success" sx={{ fontSize: 28 }} />
                            : <CancelIcon color="error" sx={{ fontSize: 28 }} />;
                          const StatusRow = ({ ok, label, value, action }) => (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                              <StatusIcon ok={ok} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body1" fontWeight="medium">{label}</Typography>
                                {value && <Typography variant="body2" color="text.secondary">{value}</Typography>}
                              </Box>
                              {!ok && action}
                            </Box>
                          );

                          const selectedContact = provisioningContactId
                            ? orgContacts.find((c) => c.id === provisioningContactId)
                            : null;
                          const selectedContactHasRequired =
                            selectedContact &&
                            (selectedContact.email || "").trim() &&
                            (selectedContact.first_name || "").trim() &&
                            (selectedContact.last_name || "").trim();
                          const canLinkCustomer = selectedContactHasRequired && !provisioningLinkLoading;

                          return (
                            <>
                              <Card>
                                <CardContent>
                                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    1. Contact & Squarespace Customer ID
                                  </Typography>
                                  {provisioningLinkError && (
                                    <Alert
                                      severity="error"
                                      sx={{ mb: 2 }}
                                      onClose={() => {
                                        setProvisioningLinkError(null);
                                        setProvisioningLinkErrorDetail(null);
                                      }}
                                    >
                                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                        Could not create Squarespace customer
                                      </Typography>
                                      <Typography variant="body2" component="div">
                                        {provisioningLinkError}
                                      </Typography>
                                      {provisioningLinkErrorDetail && (
                                        <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
                                          {provisioningLinkErrorDetail}
                                        </Typography>
                                      )}
                                    </Alert>
                                  )}
                                  {loadingContacts ? (
                                    <Typography color="text.secondary">Loading contacts…</Typography>
                                  ) : orgContacts.length === 0 ? (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                      <Typography variant="body2" color="text.secondary">
                                        No contacts for this organization. Create one to start provisioning.
                                      </Typography>
                                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 400 }}>
                                        <TextField
                                          size="small"
                                          label="First name"
                                          value={createContactFormData.first_name}
                                          onChange={(e) =>
                                            setCreateContactFormData((p) => ({ ...p, first_name: e.target.value }))
                                          }
                                          disabled={creatingContact}
                                          required
                                        />
                                        <TextField
                                          size="small"
                                          label="Last name"
                                          value={createContactFormData.last_name}
                                          onChange={(e) =>
                                            setCreateContactFormData((p) => ({ ...p, last_name: e.target.value }))
                                          }
                                          disabled={creatingContact}
                                          required
                                        />
                                        <TextField
                                          size="small"
                                          label="Email"
                                          type="email"
                                          value={createContactFormData.email}
                                          onChange={(e) =>
                                            setCreateContactFormData((p) => ({ ...p, email: e.target.value }))
                                          }
                                          disabled={creatingContact}
                                          required
                                        />
                                        <Button
                                          variant="contained"
                                          size="small"
                                          onClick={handleCreateContact}
                                          disabled={
                                            creatingContact ||
                                            !createContactFormData.first_name?.trim() ||
                                            !createContactFormData.last_name?.trim() ||
                                            !createContactFormData.email?.trim()
                                          }
                                          startIcon={creatingContact ? <CircularProgress size={16} /> : <AddIcon />}
                                        >
                                          {creatingContact ? "Creating…" : "Create Contact"}
                                        </Button>
                                      </Box>
                                      <Link
                                        component="button"
                                        variant="body2"
                                        onClick={() => navigate(`/dashboard/organizations/${subscription.organization_id}?tab=2`)}
                                        sx={{ fontWeight: 600 }}
                                      >
                                        Or go to Organization → Contacts
                                      </Link>
                                    </Box>
                                  ) : (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                      <FormControl fullWidth size="small" sx={{ maxWidth: 400 }}>
                                        <InputLabel>Select contact</InputLabel>
                                        <Select
                                          value={provisioningContactId || ""}
                                          label="Select contact"
                                          onChange={(e) => {
                                            setProvisioningContactId(e.target.value || "");
                                            setProvisioningLinkError(null);
                                            setProvisioningLinkErrorDetail(null);
                                          }}
                                        >
                                          <MenuItem value="">— Select —</MenuItem>
                                          {orgContacts.map((c) => {
                                            let m = c.meta_data;
                                            if (typeof m === "string") try { m = JSON.parse(m); } catch { m = {}; }
                                            const hasSsId = m && typeof m === "object" && m.squarespace_customer_id;
                                            const name = `${(c.first_name || "").trim()} ${(c.last_name || "").trim()}`.trim() || "Contact";
                                            const label = c.email ? `${name} (${c.email})` : name;
                                            return (
                                              <MenuItem key={c.id} value={c.id}>
                                                {hasSsId ? `${label} ✓` : label}
                                              </MenuItem>
                                            );
                                          })}
                                        </Select>
                                      </FormControl>
                                      {selectedContact && !selectedContactHasRequired && (
                                        <Typography variant="body2" color="warning.main">
                                          Selected contact needs email, first_name, and last_name to create Squarespace customer.
                                        </Typography>
                                      )}
                                      <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleLinkSquarespaceCustomer}
                                        disabled={!canLinkCustomer}
                                        startIcon={
                                          provisioningLinkLoading ? (
                                            <CircularProgress size={16} color="inherit" />
                                          ) : (
                                            <PersonSearchIcon />
                                          )
                                        }
                                      >
                                        {provisioningLinkLoading
                                          ? "Linking…"
                                          : "Create Squarespace Customer & Link"}
                                      </Button>
                                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                                        <StatusRow
                                          ok={hasContactWithSsId}
                                          label="Org has contact with squarespace_customer_id"
                                          value={
                                            hasContactWithSsId && contactWithSsId
                                              ? (() => {
                                                  let m = contactWithSsId.meta_data;
                                                  if (typeof m === "string") try { m = JSON.parse(m); } catch { m = {}; }
                                                  const sid = m?.squarespace_customer_id;
                                                  return `${contactWithSsId.email || "Contact"} — ${String(sid ?? "").slice(0, 20)}${String(sid ?? "").length > 20 ? "…" : ""}`;
                                                })()
                                              : null
                                          }
                                        />
                                        <StatusRow
                                          ok={hasSubCustomerId}
                                          label="Subscription meta_data has squarespace_customer_id"
                                          value={
                                            hasSubCustomerId
                                              ? String(subMeta.squarespace_customer_id).slice(0, 24) +
                                                (String(subMeta.squarespace_customer_id).length > 24 ? "…" : "")
                                              : null
                                          }
                                        />
                                      </Box>
                                    </Box>
                                  )}
                                </CardContent>
                              </Card>

                              <Card>
                                <CardContent>
                                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    2. Squarespace Order ID
                                  </Typography>
                                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <StatusRow
                                      ok={hasOrderId}
                                      label="Subscription meta_data has squarespace_order_id"
                                      value={hasOrderId ? String(subMeta.squarespace_order_id).slice(0, 24) + (String(subMeta.squarespace_order_id).length > 24 ? "…" : "") : null}
                                    />
                                    {!hasOrderId && hasSubCustomerId && (
                                      <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<ShoppingCartIcon />}
                                        onClick={handleOpenCreateOrderDialog}
                                      >
                                        Create Order
                                      </Button>
                                    )}
                                    {!hasOrderId && !hasSubCustomerId && (
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Complete step 1 first to link a Squarespace customer. Then create the order.
                                      </Typography>
                                    )}
                                    {!hasOrderId && hasSubCustomerId && (
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Or{" "}
                                        <Link component="button" variant="body2" onClick={() => navigate("/dashboard/squarespace/orders")}>Squarespace → Orders</Link>
                                        {" · "}
                                        <Link component="button" variant="body2" onClick={() => navigate("/dashboard/squarespace/process-order")}>Submit Order</Link>
                                      </Typography>
                                    )}
                                  </Box>
                                </CardContent>
                              </Card>

                              <Card>
                                <CardContent>
                                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    3. Squarespace Subscription ID
                                  </Typography>
                                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <StatusRow
                                      ok={hasSubId}
                                      label="Subscription meta_data has squarespace_subscription_id"
                                      value={hasSubId ? String(subMeta.squarespace_subscription_id).slice(0, 24) + (String(subMeta.squarespace_subscription_id).length > 24 ? "…" : "") : null}
                                    />
                                    {!hasSubId && hasOrderId && (
                                      <>
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          startIcon={
                                            findSubscriptionLoading ? (
                                              <CircularProgress size={16} />
                                            ) : (
                                              <RefreshIcon />
                                            )
                                          }
                                          onClick={handleFindSubscription}
                                          disabled={findSubscriptionLoading}
                                        >
                                          {findSubscriptionLoading ? "Searching…" : "Find Subscription"}
                                        </Button>
                                        {findSubscriptionError && (
                                          <Alert severity="error" sx={{ py: 0.5 }}>
                                            {findSubscriptionError}
                                          </Alert>
                                        )}
                                      </>
                                    )}
                                    {!hasSubId && !hasOrderId && (
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Complete step 2 first to create the order.
                                      </Typography>
                                    )}
                                    {!hasSubId && hasOrderId && (
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Squarespace creates the subscription asynchronously. Click Find Subscription to search, or{" "}
                                        <Link component="button" variant="body2" onClick={() => navigate("/dashboard/squarespace/subscriptions")}>Squarespace → Subscriptions</Link>
                                      </Typography>
                                    )}
                                  </Box>
                                </CardContent>
                              </Card>
                            </>
                          );
                        })()}
                      </Box>
                    )}
                  </TabPanel>
                </Card>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Squarespace website orders are linked to subscriptions.</strong> When a website order is created, the following are stored in{" "}
                    <Box component="code" sx={{ fontFamily: "monospace", fontSize: "0.9em", bgcolor: "action.hover", px: 0.5, py: 0.25, borderRadius: 0.5 }}>
                      subscription.meta_data
                    </Box>
                    :{" "}
                    <Box component="code" sx={{ fontFamily: "monospace", fontSize: "0.9em", bgcolor: "action.hover", px: 0.5, py: 0.25, borderRadius: 0.5 }}>
                      squarespace_customer_id
                    </Box>
                    ,{" "}
                    <Box component="code" sx={{ fontFamily: "monospace", fontSize: "0.9em", bgcolor: "action.hover", px: 0.5, py: 0.25, borderRadius: 0.5 }}>
                      squarespace_order_id
                    </Box>
                    ,{" "}
                    <Box component="code" sx={{ fontFamily: "monospace", fontSize: "0.9em", bgcolor: "action.hover", px: 0.5, py: 0.25, borderRadius: 0.5 }}>
                      squarespace_subscription_id
                    </Box>
                    {" "}(required for portal cancel), and{" "}
                    <Box component="code" sx={{ fontFamily: "monospace", fontSize: "0.9em", bgcolor: "action.hover", px: 0.5, py: 0.25, borderRadius: 0.5 }}>
                      pricingId
                    </Box>
                    . For older subscriptions created before this was auto-populated, add{" "}
                    <Box component="code" sx={{ fontFamily: "monospace", fontSize: "0.9em", bgcolor: "action.hover", px: 0.5, py: 0.25, borderRadius: 0.5 }}>
                      squarespace_subscription_id
                    </Box>
                    {" "}from Squarespace → Subscriptions to enable cancellation from the portal.
                  </Typography>
                </Alert>
              </>
            ) : (
              <Alert severity="warning">Subscription not found</Alert>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>

      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        onClose={handleCancelClose}
        onConfirm={handleCancelConfirm}
        loading={cancelling}
      />
      <ReactivateSubscriptionDialog
        open={reactivateDialogOpen}
        onClose={handleReactivateClose}
        onConfirm={handleReactivateConfirm}
        loading={reactivating}
      />
      <AutoRenewConfirmDialog
        open={autoRenewConfirmOpen}
        onClose={() => {
          setAutoRenewConfirmOpen(false);
          setAutoRenewPendingValue(null);
        }}
        onConfirm={handleAutoRenewConfirm}
        value={autoRenewPendingValue}
        loading={updatingAutoRenew}
      />
      <SyncFromSquarespaceConfirmDialog
        open={syncFromSsConfirmOpen}
        onClose={() => setSyncFromSsConfirmOpen(false)}
        onConfirm={handleSyncFromSquarespace}
        loading={syncFromSsLoading}
      />

      <OverrideStatusDialog
        open={overrideStatusDialogOpen}
        onClose={handleOverrideStatusClose}
        value={overrideStatusValue}
        onValueChange={setOverrideStatusValue}
        onConfirm={handleOverrideStatusConfirm}
        loading={overridingStatus}
      />

      <JsonDataModal
        open={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        data={subscription}
        title={`Subscription JSON - ${subscription ? (subscription.name || subscription.subscription_type || subscription.id || 'Unknown') : ''}`}
      />
      <JsonDataModal
        open={ssJsonModalOpen}
        onClose={() => setSsJsonModalOpen(false)}
        data={ssStatus}
        title="Squarespace Status JSON"
      />
      <CreateSquarespaceOrderDialog
        open={createOrderDialogOpen}
        onClose={() => {
          setCreateOrderDialogOpen(false);
          setCreateOrderError(null);
          setCreateOrderResponse(null);
        }}
        formData={createOrderFormData}
        onFormDataChange={setCreateOrderFormData}
        onCreate={handleCreateOrder}
        creating={creatingOrder}
        error={createOrderError}
        customerId={subscription?.meta_data?.squarespace_customer_id ? String(subscription.meta_data.squarespace_customer_id) : undefined}
        subscription={subscription}
        apiResponse={createOrderResponse}
      />
      <EditMetaDataDialog
        open={metaDataModalOpen}
        onClose={() => setMetaDataModalOpen(false)}
        value={editMetaData}
        onValueChange={setEditMetaData}
        onSave={handleSaveMetaData}
        loading={updatingMetaData}
        error={metaDataError}
        source={subscription?.source}
      />
    </>
  );
}
